import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc } from "firebase/firestore";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "@/lib/firebase";
import { getOne, createItem, updateItem } from "@/lib/adminRepository";
import ImageUploader from "@/components/admin/ImageUploader";
import { ADMIN } from "@/constants/testIds";

const BLANK = {
  title: "", awardDate: "", year: "", org: "", body: "",
  image: "", order: 99, status: "published",
};

const Label = ({ children }) => <span className="editorial-label">{children}</span>;
const Hint = ({ children }) => <span className="block text-[11px] text-olive/80 mb-1">{children}</span>;

export default function AwardForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Stable doc id so the certificate upload path is valid before first save.
  const docId = useMemo(() => (isNew ? doc(collection(db, "awards")).id : id), [isNew, id]);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOne("awards", id);
        if (cancelled) return;
        if (data) setForm({ ...BLANK, ...data });
        else toast.error("Award not found.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[award] load failed:", err);
        toast.error("Failed to load award.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      const { id: _id, ...rest } = form;
      // Derive a display year from the award date when not set explicitly.
      const year = form.year || (form.awardDate ? String(form.awardDate).slice(0, 4) : "");
      const payload = { ...rest, year, order: Number(form.order) || 99 };
      if (isNew) {
        await createItem("awards", payload, docId);
        toast.success("Award created.");
      } else {
        await updateItem("awards", id, payload);
        toast.success("Award saved.");
      }
      navigate("/admin/awards");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[award] save failed:", err);
      toast.error("Save failed: " + (err?.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-olive">Loading…</p>;

  return (
    <div data-testid="admin-award-form">
      <Link to="/admin/awards" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to awards
      </Link>
      <header className="mb-7">
        <div className="eyebrow text-bronze mb-1">{isNew ? "New" : "Edit"}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">{isNew ? "New Award" : form.title || "Edit award"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr,300px] gap-6">
        {/* Main */}
        <div className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Title *</Label>
              <input className="editorial-input" value={form.title} onChange={set("title")} data-testid="award-title" placeholder="e.g. Top Producer 2024" />
            </label>
            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>Award date</Label>
                <Hint>Drives the order — newest first on /awards.</Hint>
                <input type="date" className="editorial-input" value={form.awardDate} onChange={set("awardDate")} data-testid="award-date" />
              </label>
              <label className="block">
                <Label>Organisation</Label>
                <input className="editorial-input" value={form.org} onChange={set("org")} data-testid="award-org" placeholder="ERA Realty" />
              </label>
            </div>
            <label className="block">
              <Label>Caption / description</Label>
              <textarea rows={3} className="editorial-input resize-y" value={form.body} onChange={set("body")} data-testid="award-body" placeholder="Optional context shown on the text fallback card." />
            </label>
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Certificate</div>
            <h2 className="font-serif text-lg text-ink mb-3">Award image</h2>
            <ImageUploader path={`media/awards/${docId}`} value={form.image ? [form.image] : []} onChange={(urls) => set("image")(urls[0] || "")} single testid="award-image" />
            <p className="text-[11px] text-olive mt-3">Upload the certificate/plaque scan — shown image-first in the masonry. Without one, a text card is shown instead.</p>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Visibility</Label>
              <select className="editorial-input" value={form.status} onChange={set("status")} data-testid="award-status">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </label>
            <label className="block">
              <Label>Year (display)</Label>
              <Hint>Optional — auto-filled from the award date if blank.</Hint>
              <input className="editorial-input" value={form.year} onChange={set("year")} data-testid="award-year" placeholder="2024" />
            </label>
            <label className="block">
              <Label>Sort tiebreak</Label>
              <input type="number" className="editorial-input" value={form.order} onChange={set("order")} data-testid="award-order" />
            </label>
          </section>

          <div className="flex gap-3">
            <Link to="/admin/awards" className="btn-secondary flex-1 text-center">Cancel</Link>
            <button type="submit" disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary flex-1 justify-center disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
